
import { PROJECT_ENDPOINTS } from '../config/apiEndpoints';
import axios from 'axios';
import useAuth from './useAuth';
import { IProject } from '../data_models/project';
import { IEmployer } from '../data_models/employer';
import { IFreelancer } from '../data_models/freelancer';
import { downloadFileFromSignedUrl } from '../utils/downloadFiles';
import { EmployerWallet } from '../data_models/employerWallet';
import { ITask } from '../data_models/task';
import { FreelancerWallet } from '../data_models/freelancerWallet';

function useProjects() {

    const { authToken } = useAuth();

    const fetchProjectById = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.get(`${PROJECT_ENDPOINTS.GET_PROJECT_BY_ID}/${id}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchProjectsByFilter = async (filter: any, page: number, limit: number): Promise<{ project: IProject, employerBrief: IEmployer, pmBrief: IFreelancer, team: IFreelancer[] }[]> => {
        try {
            const response = await axios.post(PROJECT_ENDPOINTS.POST_GET_PROJECTS_BY_FILTER, {
                filter: filter,
                page: page,
                limit: limit
            }, {
                headers: {
                    Authorization: `${authToken}`,
                },

            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchMyProjects = async (page: number, limit: number): Promise<IProject[]> => {
        try {
            const response = await axios.get(PROJECT_ENDPOINTS.GET_MY_PROJECTS, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }


    const fetchMyProjectsAsPm = async (page: number, limit: number): Promise<IProject[]> => {
        try {
            const response = await axios.get(PROJECT_ENDPOINTS.GET_MY_PROJECTS_AS_PM, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchMyUnassignedProjects = async (page: number, limit: number): Promise<IProject[]> => {
        try {
            const response = await axios.get(PROJECT_ENDPOINTS.GET_MY_UNASSIGNED_PROJECTS, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const suggestRefinedProjectDetails = async (projectId: string): Promise<IProject> => {
        try {
            const response = await axios.get(`${PROJECT_ENDPOINTS.GET_PROJECT_AI_SUGGEST_REFINED_DETAILS}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const potentialProjectDetailDefinitionWeaknesses = async (projectId: string): Promise<string[]> => {
        try {
            const response = await axios.get(`${PROJECT_ENDPOINTS.GET_PROJECT_AI_POTENTIAL_WEAKNESSES}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const createProjectDraft = async (project: IProject): Promise<IProject> => {
        try {
            const response = await axios.post(PROJECT_ENDPOINTS.POST_PROJECT, { project }, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const updateProjectDetails = async (project: IProject): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_UPDATE_DETAILS}`,
                { project },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const publishProject = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_PUBLISH}/${id}`, {}, {

                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        }
        catch (error: any) {
            if (error.response && error.response.data) {
                throw new Error(error.response.data.error);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const startProjectSelfManaged = async (id: string): Promise<{ project: IProject, tasks: ITask[] }> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_START_SELF_MANAGED}/${id}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        }
        catch (error: any) {
            if (error.response && error.response.data) {
                throw new Error(error.response.data.error);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const startProjectWithPm = async (id: string): Promise<{ project: IProject, tasks: ITask[], projectManager?: IFreelancer }> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_START_WITH_PM}/${id}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        }
        catch (error: any) {
            if (error.response && error.response.data) {
                throw new Error(error.response.data.error);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestProjectApproval = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_REQUEST_APPROVAL}/${id}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        }
        catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const rejectApprovalRequest = async (id: string, reason?: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_REJECT_APPROVAL_REQUEST}/${id}`,
                {
                    reason
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        }
        catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const approveProjectDetails = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_APPROVE_DETAILS}/${id}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        }
        catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const requestProjectCompletion = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_REQUEST_COMPLETION}/${id}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        }
        catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const rejectCompletionRequest = async (id: string, reason: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_REJECT_COMPLETION_REQUEST}/${id}`,
                {
                    reason
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        }
        catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const completeProject = async (id: string): Promise<IProject> => {
        try {
            const response = await axios.put(`${PROJECT_ENDPOINTS.PUT_PROJECT_COMPLETE}/${id}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const deleteProjectDraft = async (id: string): Promise<void> => {
        try {

            const response = await axios.delete(`${PROJECT_ENDPOINTS.DELETE_PROJECT_DRAFT}/${id}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const createAndDownloadProjectRoadmapPdf = async (projectId: string): Promise<void> => {
        try {
            const response = await axios.post(`${PROJECT_ENDPOINTS.POST_PROJECT_ROADMAP_PDF}/${projectId}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });

            const url = response.data.signedDownloadUrl;
            const filename = response.data.filename;
            downloadFileFromSignedUrl(url, filename);
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const triggerRoadmapVerificationByHumanPm = async (projectId: string): Promise<{project: IProject, tasks?: ITask[]}> => {
        try {
            const response = await axios.post(`${PROJECT_ENDPOINTS.POST_PROJECT_ROADMAP_VERIFICATION_TRIGGER}/${projectId}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const verifyProjectRoadmap = async (projectId: string): Promise<{project: IProject, wallet: FreelancerWallet}> => {
        try {
            const response = await axios.post(`${PROJECT_ENDPOINTS.POST_PROJECT_ROADMAP_VERIFICATION_VERIFY}/${projectId}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    const roadmapVerificationPmAgreeToContinue = async (projectId: string, agreeToContinue: boolean): Promise<IProject> => {
        try {
            const response = await axios.post(`${PROJECT_ENDPOINTS.POST_PROJECT_ROADMAP_VERIFICATION_PM_AGREE_TO_CONTINUE}/${projectId}`, { agreeToContinue }, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    return {
        fetchProjectById,
        fetchProjectsByFilter,
        fetchMyProjects,
        fetchMyProjectsAsPm,
        fetchMyUnassignedProjects,
        suggestRefinedProjectDetails,
        potentialProjectDetailDefinitionWeaknesses,
        createProjectDraft,
        updateProjectDetails,
        publishProject,
        startProjectSelfManaged,
        startProjectWithPm,
        requestProjectApproval,
        rejectApprovalRequest,
        approveProjectDetails,
        requestProjectCompletion,
        rejectCompletionRequest,
        completeProject,
        deleteProjectDraft,
        createAndDownloadProjectRoadmapPdf,
        triggerRoadmapVerificationByHumanPm,
        verifyProjectRoadmap,
        roadmapVerificationPmAgreeToContinue,
    };
}

export default useProjects;
